import * as Sentry from '@sentry/react'
import { RouterHistory } from '@sentry/react/types/reactrouter'
import { Integrations } from '@sentry/tracing'
import { History } from 'history'

import { version } from '../../../../../../package.json'
import { IErrorLogger, severityLevel } from '../../types'

export class SentryAdapter implements IErrorLogger {
  history: History | null = null

  getNavigationHistory(history: History): void {
    this.history = history
  }

  init(): void {
    const ignoredErrors = [
      '401 Unauthorized',
      'Request failed with status code 401',
      '422 Unprocessable Entity',
      'Request failed with status code 422',
    ]

    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      normalizeDepth: 10,
      integrations: [
        new Integrations.BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV5Instrumentation(
            this.history as unknown as RouterHistory,
          ),
        }),
      ],
      environment: process.env.REACT_APP_SENTRY_ENV,
      release: version,
      beforeSend(event, hint) {
        const error = hint.originalException as any
        event.message = error.message
        return event
      },
      ignoreErrors: ignoredErrors,
      tracesSampleRate: 0.1,
    })
  }

  captureException(
    transactionName: string,
    severityLevel: severityLevel,
    error: Error,
    details: Record<string, any>,
  ): void {
    Sentry.withScope((scope) => {
      scope.setLevel(severityLevel)
      scope.setTransactionName(transactionName)
      if (details) {
        scope.setExtra('Error Details', details)
      }
      Sentry.captureException(error)
    })
  }

  captureLog(
    message: string,
    severityLevel: severityLevel,
    details: Record<string, any>,
  ): void {
    Sentry.withScope((scope) => {
      scope.setLevel(severityLevel)
      scope.setTransactionName(message)
      if (details) {
        scope.setExtra('Details', details)
      }
      scope.setFingerprint([message, severityLevel])
      Sentry.captureMessage(message)
    })
  }

  captureUIException(error: Error, { componentStack }: React.ErrorInfo): void {
    Sentry.withScope((scope) => {
      const errorBoundaryError = new Error(error.message)
      errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`
      errorBoundaryError.stack = componentStack

      scope.setTag('Origem', 'ErrorBoundary')
      scope.setExtra('Cause', errorBoundaryError)
      scope.setExtra('stack', error.stack)
      scope.setExtra('Componentstack', componentStack)
      scope.setExtra('Name', error.name)
      scope.setExtra('ErrorMessage', error.message)
      Sentry.configureScope((scope) =>
        scope.setTransactionName('Capturado com Error Boundary'),
      )
      const eventId = Sentry.captureException(error, {
        contexts: { react: { componentStack } },
      })
      scope.setExtra('eventId', eventId)
    })
  }

  captureHTTPException(err: any): void {
    Sentry.withScope((scope: Sentry.Scope) => {
      const requestUrl = err?.config?.url
      const statusCode = err?.response?.status
      const requestMethod = err?.config?.method
      const errorMessage = err?.response?.data?.error
      const genericErrorMessage = err?.message
      scope.setFingerprint([requestUrl, requestMethod, String(statusCode)])
      scope.setExtra('requestUrl', requestUrl)
      scope.setExtra('statusCode', statusCode)
      scope.setExtra('requestMethod', requestMethod)
      scope.setExtra('errorMessage', errorMessage)
      scope.setExtra('genericErrorMessage', genericErrorMessage)
      scope.setExtra('date', new Date())
      scope.setTag('requestUrl', requestUrl)
      scope.setTag('statusCode', statusCode)
      scope.setTag('requestMethod', requestMethod)
      Sentry.configureScope((scope: Sentry.Scope) =>
        scope.setTransactionName('Axios request error'),
      )
      Sentry.captureException(err)
    })
  }
}
